import { AuthClient, BaseErrorResponse } from 'api/base';
import clsx from 'clsx';
import { GET_ENUMS, GET_PARAMS, PROFILE_PATHNAME } from 'const';
import {
  $savedAddresses,
  getSavedAddressesFx,
  SavedAddress,
} from 'domains/savedAddresses';
import {
  $address,
  $allCitiesList,
  $citiesList,
  $city,
  $country,
  $countriesList,
  changeCity,
  changeCountry,
  submitAddress,
} from 'domains/cartography';
import { closeStartPage } from 'domains/application';
import { usePrepareLink } from 'hooks/router';
import { useUnit } from 'effector-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { FEBanner, FEButton, FESpinner } from 'ui-kit/components';
import { useMediaQuery } from 'ui-kit/hooks';
import { AddressRow } from './AddressRow';
import { Empty } from './Empty';
import { isSameDeliveryAddress } from './lib/formatAddress';
import style from './SavedAddresses.module.css';

export const SavedAddresses = () => {
  const { t } = useTranslation();
  const is500 = useMediaQuery('(min-width: 500px)');
  const navigate = useNavigate();

  const [
    addresses,
    isPending,
    currentAddress,
    city,
    country,
    citiesList,
    allCitiesList,
    countriesList,
    onChangeCity,
    onChangeCountry,
    onSubmitAddress,
    onCloseStartPage,
    loadAddresses,
  ] = useUnit([
    $savedAddresses,
    getSavedAddressesFx.pending,
    $address,
    $city,
    $country,
    $citiesList,
    $allCitiesList,
    $countriesList,
    changeCity,
    changeCountry,
    submitAddress,
    closeStartPage,
    getSavedAddressesFx,
  ]);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(addresses.length > 0);

  const isAuth = AuthClient.isAuth;

  const cartographyLink = usePrepareLink({
    query: {
      [GET_PARAMS.page]: GET_ENUMS.page.cartography,
    },
    keepOldQuery: true,
  });

  const openCartography = () => navigate(cartographyLink);

  useEffect(() => {
    if (!isAuth) return;
    setLoadError(null);
    loadAddresses()
      .then(() => setHasLoaded(true))
      .catch((error: BaseErrorResponse.ErrorResponse) => {
        setHasLoaded(true);
        setLoadError(
          error?.errors?.[0]?.presentationData?.message ||
            t('profile.addressesLoadError'),
        );
      });
  }, [isAuth, loadAddresses, t]);

  const goBack = () => {
    const isDefault =
      ((window.history.state && window.history.state.key) || 'default') ===
      'default';

    if (isDefault) return navigate(PROFILE_PATHNAME, { replace: true });
    return navigate(-1);
  };

  const handleSelect = (item: SavedAddress) => {
    if (city?.id !== item.cityId) {
      const nextCity =
        allCitiesList.find((c) => c.id === item.cityId) ||
        citiesList.find((c) => c.id === item.cityId);

      if (nextCity) {
        if (country?.id !== nextCity.countryId) {
          const nextCountry = countriesList.find(
            (c) => c.id === nextCity.countryId,
          );
          if (nextCountry) {
            onChangeCountry(nextCountry);
          }
        }
        onChangeCity(nextCity);
      }
    }

    onSubmitAddress({
      currentAddress: {
        address: item.address,
        street: item.street,
        house: item.house,
        housing: item.housing,
        cityId: item.cityId,
        location: item.location,
        details: item.details,
        entrance: item.entrance,
        apartment: item.apartment,
        floor: item.floor,
      },
    });

    onCloseStartPage();
    navigate('/', { replace: true });
    window.scrollTo({ top: 0 });
  };

  const showEmpty = hasLoaded && !isPending && !loadError && !addresses.length;
  const showList = !loadError && addresses.length > 0;

  return (
    <>
      {!isAuth && <Navigate to={'/?page=auth'} replace />}
      {isAuth && (
        <div className={style.page}>
          {/* <div className={style.pageHeader}>
            <button
              onClick={goBack}
              className={clsx('button-arrowBack', style.backButton)}
            />
            <h2 className={is500 ? 'title' : 'headline'}>
              {t('profile.addressesDetailsTitle')}
            </h2>
          </div> */}

          <div className={style.content}>
            {isPending && !addresses.length && (
              <div className={style.loader}>
                <FESpinner />
              </div>
            )}

            {loadError && (
              <div className={style.card}>
                <FEBanner text={loadError} />
                <FEButton
                  className={style.retryButton}
                  type='secondary'
                  onClick={() => {
                    setLoadError(null);
                    loadAddresses()
                      .then(() => setHasLoaded(true))
                      .catch((error: BaseErrorResponse.ErrorResponse) => {
                        setLoadError(
                          error?.errors?.[0]?.presentationData?.message ||
                            t('profile.addressesLoadError'),
                        );
                      });
                  }}
                >
                  {t('profile.addressesRetry')}
                </FEButton>
              </div>
            )}

            {showEmpty && <Empty onAdd={openCartography} />}

            {showList && (
              <div className={style.card}>
                <div className={style.list}>
                  {addresses.map((item, index) => (
                    <div key={item.id}>
                      {index > 0 && <div className={style.divider} />}
                      <AddressRow
                        item={item}
                        selected={isSameDeliveryAddress(item, currentAddress)}
                        onSelect={() => handleSelect(item)}
                      />
                    </div>
                  ))}
                </div>
                <FEButton
                  className={style.addButton}
                  type='secondary'
                  onClick={openCartography}
                >
                  {t('profile.addressesAdd')}
                </FEButton>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
